import { ArrowRight } from 'lucide-react';
import { Button } from '../../../design-system/button/button';
import { Inline } from '../../../design-system/inline/inline';
import { Stack } from '../../../design-system/stack/stack';
import { BeneficiaryView } from '../../../types/entity-types';
import { PaymentInitiationCard } from '../components/payment-initiation-card';
import { getLatestDebitTransferDate } from '../utils/get-latest-debit-transfer-date';
import { AllBeneficiariesTable } from './all-beneficiaries-table';
import { LastPaidBeneficiariesTable } from './last-paid-beneficiaries-table';

type ViewAllRecipientsConfig =
  | {
      visible: false;
    }
  | {
      visible: true;
      onGoBack: () => void;
    };

type PaymentsSectionProps = {
  beneficiaries: BeneficiaryView[];
  viewAllRecipientsConfig: ViewAllRecipientsConfig;
  onInitiateBillPay: () => void;
  onInitiateInternalTransfer: () => void;
  onInitiateExternalTransfer: () => void;
  onInitiateBeneficiaryPayment: (beneficiaryId: string) => void;
  onViewAllRecipients: () => void;
};

export const PaymentsSection = ({
  beneficiaries,
  viewAllRecipientsConfig,
  onInitiateBillPay,
  onInitiateInternalTransfer,
  onInitiateExternalTransfer,
  onInitiateBeneficiaryPayment,
  onViewAllRecipients,
}: PaymentsSectionProps) => {
  if (viewAllRecipientsConfig.visible) {
    return (
      <AllBeneficiariesTable
        beneficiaries={beneficiaries}
        onGoBack={viewAllRecipientsConfig.onGoBack}
        onInitiateBeneficiaryPayment={onInitiateBeneficiaryPayment}
      />
    );
  }

  const beneficiariesWithLastPaid = beneficiaries.filter((b) => {
    const latestDebitTransferDate = getLatestDebitTransferDate({
      transactions: b.transactions,
    });

    return !!latestDebitTransferDate;
  });

  return (
    <Stack align="stretch" space={10} className="bg-white px-6 pt-4 pb-5">
      <Inline justify="between" className="h-[42px]">
        <h1 className="text-2xl font-semibold text-black">Payments</h1>
      </Inline>

      <Inline space={3} wrap="nowrap" align="stretch" className="gap-y-3">
        <PaymentInitiationCard variant="BILL_PAY" onInitiatePayment={onInitiateBillPay} />

        <PaymentInitiationCard variant="EXTERNAL_TRANSFER" onInitiatePayment={onInitiateExternalTransfer} />

        <PaymentInitiationCard variant="INTERNAL_TRANSFER" onInitiatePayment={onInitiateInternalTransfer} />
      </Inline>

      {beneficiaries.length > 0 && (
        <Stack align="stretch" space={4}>
          <h2 className="text-lg font-semibold text-black">Most recent recipients</h2>

          <LastPaidBeneficiariesTable
            beneficiaries={beneficiariesWithLastPaid}
            onInitiateBeneficiaryPayment={onInitiateBeneficiaryPayment}
          />

          <Stack>
            <Button
              variant="link"
              iconRight={<ArrowRight className="h-4 w-4" />}
              size="none"
              className="text-link"
              onClick={onViewAllRecipients}
            >
              View all recipients
            </Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
